'use client';

import React, { useState, useCallback } from 'react';
import styles from './FAQPage.module.css';

interface FAQQuestion {
  question: string;
  answer: string;
}

interface FAQCategory {
  id: string;
  title: string;
  icon: string;
  questions: FAQQuestion[];
}

interface FAQPageClientProps {
  categories: FAQCategory[];
}

export default function FAQPageClient({ categories }: FAQPageClientProps) {
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [openItems, setOpenItems] = useState<Record<string, number | null>>({});

  const toggleItem = useCallback((categoryId: string, index: number) => {
    setOpenItems(prev => ({
      ...prev,
      [categoryId]: prev[categoryId] === index ? null : index,
    }));
  }, []);

  const filteredCategories = activeCategory
    ? categories.filter(c => c.id === activeCategory)
    : categories;

  return (
    <>
      <nav className={styles.categoryNav} aria-label="FAQ categories">
        <button
          className={`${styles.categoryButton} ${activeCategory === null ? styles.categoryButtonActive : ''}`}
          onClick={() => setActiveCategory(null)}
        >
          All
          <span className={styles.questionCount}>
            {categories.reduce((sum, c) => sum + c.questions.length, 0)}
          </span>
        </button>
        {categories.map(cat => (
          <button
            key={cat.id}
            className={`${styles.categoryButton} ${activeCategory === cat.id ? styles.categoryButtonActive : ''}`}
            onClick={() => setActiveCategory(cat.id)}
          >
            {cat.title}
            <span className={styles.questionCount}>{cat.questions.length}</span>
          </button>
        ))}
      </nav>

      {filteredCategories.map(category => (
        <section key={category.id} className={styles.categorySection}>
          <h2 className={styles.categoryTitle}>{category.title}</h2>
          <div className={styles.faqList}>
            {category.questions.map((item, index) => {
              const isOpen = openItems[category.id] === index;
              return (
                <div
                  key={index}
                  className={`${styles.faqItem} ${isOpen ? styles.open : ''}`}
                >
                  <button
                    className={styles.question}
                    onClick={() => toggleItem(category.id, index)}
                    aria-expanded={isOpen}
                  >
                    <span className={styles.questionText}>{item.question}</span>
                    <span className={styles.icon}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <defs>
                          <linearGradient id={`faqGrad-${category.id}-${index}`} x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#ff9966" />
                            <stop offset="100%" stopColor="#ff5e62" />
                          </linearGradient>
                        </defs>
                        <path
                          d="M12 5V19M5 12H19"
                          stroke={`url(#faqGrad-${category.id}-${index})`}
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  </button>
                  <div className={styles.answerWrapper}>
                    <div className={styles.answer}>
                      <p className={styles.answerText}>{item.answer}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </>
  );
}
